<script setup>
  const localePath = useLocalePath()
  const { locale } = useI18n()
  const dayjs = useDayjs()

  const props = defineProps({
    item: { type: Object, required: true },
    query: { type: String, default: '' }
  })

  const emit = defineEmits(['navigate'])

  const typeColors = {
    articles: 'sky',
    events: 'teal',
    publications: 'indigo',
    actions: 'amber',
    podcasts: 'purple',
    commissions: 'green'
  }

  // #region Local state

  const badgeColor = computed(() => typeColors[props.item.type] || 'slate')

  const formattedDate = computed(() => {
    if (!props.item.date) return null
    return dayjs(props.item.date).locale(locale.value).format('LL')
  })

  const itemRoute = computed(() => {
    const { type, slug, documentId } = props.item

    const routeMap = {
      articles: slug ? { name: 'articles-slug', params: { slug } } : null,
      events: slug ? { name: 'events-slug', params: { slug } } : null,
      actions: slug ? { name: 'actions-slug', params: { slug } } : null,
      commissions: slug ? { name: 'commissions-slug', params: { slug } } : null,
      publications: documentId ? { name: 'publications-documentId', params: { documentId } } : null,
      podcasts: documentId ? { name: 'pages-podcasts', query: { highlight: documentId } } : null
    }
    const route = routeMap[type]

    return route ? localePath(route) : null
  })

  const highlightedTitle = computed(() => {
    if (!props.query || props.query.length < 2) return props.item.title

    const words = props.query
      .trim()
      .split(/\s+/)
      .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    const regex = new RegExp(`(${words.join('|')})`, 'gi')

    return props.item.title.replace(regex, '<mark class="bg-sky-100 text-sky-800 rounded">$1</mark>')
  })

  // #endregion

  // #region Methods

  function handleClick() {
    emit('navigate')
  }

  // #endregion
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <div
    class="relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 hover:bg-slate-50 group cursor-pointer"
  >
    <div class="flex-1 min-w-0">
      <NuxtLink v-if="itemRoute" :to="itemRoute" class="expanded-target" @click="handleClick">
        <p
          class="text-sm font-medium text-slate-700 group-hover:text-sky-700 transition-colors duration-200 line-clamp-1"
          v-html="highlightedTitle"
        />
      </NuxtLink>
      <p
        v-else
        class="text-sm font-medium text-slate-700 group-hover:text-sky-700 transition-colors duration-200 line-clamp-1"
        v-html="highlightedTitle"
      />
      <div class="flex items-center gap-2 mt-0.5">
        <CommonBadge :color="badgeColor" :interactive="false" class="text-[10px]! px-1.5! py-0.5!">
          {{ $t(`globalSearch.types.${item.type}`) }}
        </CommonBadge>
        <span v-if="formattedDate" class="text-xs text-slate-400">
          {{ formattedDate }}
        </span>
        <span v-if="item.category" class="text-xs text-slate-400 truncate"> · {{ item.category }} </span>
      </div>
    </div>

    <font-awesome
      v-if="itemRoute"
      class="text-xs text-slate-300 group-hover:text-sky-500 transition-all duration-200 group-hover:translate-x-0.5 shrink-0"
      :icon="['fas', 'chevron-right']"
    />
  </div>
</template>
